import { logSwallowed } from '@/lib/errors';
import { memo, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { MentionRichText } from '@/components/chat/MentionRichText';
import { MessageCircle, Pin } from 'lucide-react';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { MarkdownView } from '@/components/viewer/FileViewer';
import { FilePreviewModal } from '@/components/viewer/FilePreviewModal';
import { ensureReactionPictures, subscribeReactionPictures } from '@/lib/reactionPictures';
import { mediaKind, isVoiceMessage } from '@/lib/fileName';
import { MessageAttachments } from '@/components/chat/MessageAttachments';
import { ChatTranscript } from '@/components/chat/ChatTranscript';
import { messageBubbleStyle, type MessageBubbleLayout } from '@/lib/chatMessageLayout';
import { parseGeoMessage } from '@/lib/geo/geoMessage';
import { GeoMessagePreview } from '@/components/geo/GeoMessagePreview';
import { looksLikeMarkdown } from '@/lib/chatText';
import { MessageReactionsRow } from '@/components/chat/MessageReactionsRow';
import { MessageSelectMarker } from '@/components/chat/chatSelection';
import { AnswerSources } from '@/components/chat/AnswerSources';
import { parseAssistantSources, stripAssistantSources } from '@/lib/assistantSources';
import { MessageActionsPanel } from '@/components/chat/MessageActionsPanel';
import { ReactionPicker } from '@/components/chat/ReactionPicker';


export interface Msg {
  id: string;
  conversationId: string;
  senderId: string;
  text: string;
  createdAt: string;
  isDeleted?: boolean;
  readBy?: string[];
  attachments?: { id: string; name: string; size: number; fileType?: string }[];
  replyTo?: string;
  reactions?: { emoji: string; userId: string }[];
  /** D2: отметка «изменено» (момент последней правки) */
  editedAt?: string;
  /** D2: история правок (прежние версии текста, новые в конце) */
  editHistory?: { text: string; editedAt: string }[];
  /** D3: число ответов треда («N ответов» под сообщением) */
  replyCount?: number;
  /** D6: сообщение закреплено в чате (правая панель «Закреплённые») */
  isPinned?: boolean;
}

interface MessageBubbleProps {
  m: Msg;
  prevMsg: Msg | null;
  myId: string;
  isDark: boolean;
  senderName: (id: string) => string;
  /** avatarUrl (fileId) отправителя для этого бабла (из списка участников) */
  avatarUrl?: string;
  /** логины УЧАСТНИКОВ диалога — подсветка упоминаний «@login» (пусто — без подсветки) */
  mentionLogins?: string[];
  /** мой логин (упоминание вас выделяется заметнее) */
  myLogin?: string;
  replySourceText?: string; // текст исходного сообщения при ответе
  onReply: (id: string) => void;
  onReactToggle: (m: Msg, emoji: string) => void;
  onForward: (m: Msg) => void;
  /** D2: «Изменить» для своих сообщений (правка с историей) */
  onEdit?: (m: Msg) => void;
  /** «Удалить» (скрыть у себя — сервер, scope=self) */
  onDelete?: (m: Msg) => void;
  /** «…» — открыть то же контекстное меню (для тач-экранов, где нет ПКМ) */
  onMore?: (m: Msg, x: number, y: number) => void;
  /** D3: открыть обсуждение (тред) — по бейджу «N ответов» */
  onOpenThread?: (m: Msg) => void;
  /** D6: закрепить/открепить сообщение (toggle серверного is_pinned) */
  onPinToggle?: (m: Msg, pinned: boolean) => void;
  onDownload: (id: string, name: string) => void;
  /** Режим множественного выбора: тап по баблу выбирает/снимает (§2.3) */
  selectMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  /** «Слушать подряд»: fileId аудио, которому очередь просит автозапуск */
  autoPlayFileId?: string | null;
  /** Голосовое/аудио доиграло — очередь запросит следующее аудио ленты */
  onAudioEnded?: (fileId: string) => void;
  /** Пользователь остановил/перемотал — отмена автозапуска */
  onAudioInterrupt?: () => void;
  /** Автозапуск состоялся — очередь сбрасывает цель */
  onAudioAutoplayConsumed?: (fileId: string) => void;
  /** Подсветка совпадений поиска в тексте (сообщение, к которому перешли) */
  highlightQuery?: string;
  /** Переход из поиска: заметно выделить сам пузырь */
  highlighted?: boolean;
  /** Ответ бота-ассистента: показываем блок «Источники» (в т.ч. «недоступны»). */
  assistantAnswer?: boolean;
}

// Коды реакций = metadata_pictures.code БЕЗ ИЗМЕНЕНИЙ (SSoT — ветка
// «Картинки», порядок — REACTION_PICTURE_ORDER); библиотека расширяемая —
// дополнения берём динамически (knownReactionCodes). В chat_messages
// .reactions[].emoji хранится ТОЛЬКО код (легаси нормализует сервер).

// Тач-устройства (без наведения мыши): панель действий по тапу на сообщение
const TAP_SHOWS_ACTIONS = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

function MessageBubbleInner({ m, prevMsg, myId, isDark, senderName, avatarUrl, mentionLogins, myLogin, replySourceText, onReply, onReactToggle, onForward, onEdit, onDelete, onMore, onOpenThread, onPinToggle, onDownload, selectMode, selected, onToggleSelect, autoPlayFileId, onAudioEnded, onAudioInterrupt, onAudioAutoplayConsumed, highlightQuery = '', highlighted = false, assistantAnswer = false }: MessageBubbleProps) {
  const mine = m.senderId === myId;
  // Служебный маркер источников вырезается из текста ВСЕГДА; сам блок
  // разворачивается только для ответа бота-ассистента (защита от подделки).
  const parsedText = assistantAnswer
    ? parseAssistantSources(m.text)
    : { text: stripAssistantSources(m.text), sources: null, marker: false };
  const bodyText = parsedText.text;
  const [pickerOpen, setPickerOpen] = useState(false);
  // Панель действий, открытая тапом (тач-устройства); на десктопе — hover
  const [actionsOpen, setActionsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Перерисовка, когда SVG реакций доедут из Картинок (однократная загрузка)
  const [rxVersion, setRxVersion] = useState(0);
  useEffect(() => {
    const un = subscribeReactionPictures(() => setRxVersion((v) => v + 1));
    ensureReactionPictures().catch((e) => { logSwallowed(e, 'components/chat/MessageBubble.tsx:reactionPictures'); return undefined; });
    return un;
  }, []);
  // Закрытие панели/пикера при тапе/клике ВНЕ сообщения
  useEffect(() => {
    if (!actionsOpen && !pickerOpen) return;
    const onDown = (ev: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(ev.target as Node)) {
        setActionsOpen(false);
        setPickerOpen(false);
      }
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [actionsOpen, pickerOpen]);
  const closeActions = () => { setActionsOpen(false); setPickerOpen(false); };
  const handleBubbleClick = (e: ReactMouseEvent) => {
    // Тап по сообщению (тач) — показать/скрыть панель действий; кнопки и
    // элементы ввода/медиа не считаются «тапом по сообщению».
    if (!TAP_SHOWS_ACTIONS || selectMode) return;
    const t = e.target as HTMLElement | null;
    if (t?.closest('button, a, input, textarea, video, audio')) return;
    if (actionsOpen) closeActions();
    else setActionsOpen(true);
  };
  const [previewFile, setPreviewFile] = useState<{ fileId: string; fileName: string } | null>(null);
  const openPreview = (fileId: string, name: string) => setPreviewFile({ fileId, fileName: name });

  const currDate = new Date(m.createdAt);
  const prevDate = prevMsg ? new Date(prevMsg.createdAt) : null;
  const showDateSep = !prevMsg || currDate.toDateString() !== prevDate?.toDateString();
  const grouped = !!prevMsg && prevMsg.senderId === m.senderId && !showDateSep && !prevMsg.isDeleted && (currDate.getTime() - (prevDate?.getTime() || 0) < 5 * 60 * 1000);

  // Раскладка пузыря — ЕДИНЫЙ расчёт (lib/chatMessageLayout): текст/подпись
  // до 560px, медиа до 460px; одно изображение — по пропорции картинки.
  // Никаких ветвлений по источнику сообщения — только по составу вложений.
  const attachments = m.attachments || [];
  const attachmentKinds = attachments.map((a) => mediaKind(a.name, a.fileType));
  const hasMediaAttachment = attachmentKinds.some((k) => k === 'image' || k === 'video' || k === 'audio');
  const singleImage = attachments.length === 1 && attachmentKinds[0] === 'image';
  // Голосовое с расшифровкой: текст — ПОД аудио (Telegram-стиль), не в теле бабла.
  const hasVoiceAttachment = attachments.some((a) => isVoiceMessage(a.name, a.fileType));
  const voiceTranscript = hasVoiceAttachment && !!bodyText;
  const bubbleLayout: MessageBubbleLayout = singleImage
    ? 'single-image'
    : hasMediaAttachment ? 'media' : 'text';

  if (m.isDeleted && !bodyText) {
    return (
      <div className="flex justify-center pt-1">
        <div className="text-[10px] text-muted-foreground italic px-2 py-1 bg-muted/40 rounded-full backdrop-blur">Сообщение удалено</div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="px-3 w-full min-w-0"
      data-msg-bubble
      data-msg-id={m.id}
      onClick={handleBubbleClick}
      onClickCapture={selectMode && onToggleSelect
        ? (e) => { e.preventDefault(); e.stopPropagation(); onToggleSelect(m.id); }
        : undefined}
    >
        {showDateSep && (
          // Разделитель дня (НЕ sticky — в виртуализации прилипание не работает;
          // закреплённая дата просматриваемого фрагмента — оверлей в ChatThread)
          <div className="flex items-center gap-3 pt-3 pb-1 pointer-events-none">
            <span className="h-px flex-1" style={{ background: 'color-mix(in oklch, var(--color-border) 50%, transparent)' }} />
            <span className="text-[10.5px] font-semibold tracking-wider text-muted-foreground whitespace-nowrap">
              {currDate.toLocaleDateString('ru-RU', {
                day: 'numeric', month: 'long',
                year: currDate.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
              })}
            </span>
            <span className="h-px flex-1" style={{ background: 'color-mix(in oklch, var(--color-border) 50%, transparent)' }} />
          </div>
        )}
      <div className="flex flex-col">
      {/* ФИД-СТИЛЬ: ВСЕ баблы слева (на широких экранах взгляд не бегает);
          свои различаются цветом/чёткостью, колонка 32px единая для ритма */}
      <div className={`flex w-full min-w-0 justify-start group items-end gap-1.5 ${grouped ? 'pt-0.5' : 'pt-1.5'}`}>
      {selectMode && <MessageSelectMarker selected={!!selected} />}
      <span className="block h-8 w-8 shrink-0">
        {!mine && !grouped && (
          <UserAvatar uid={m.senderId} fileId={avatarUrl} name={senderName(m.senderId) || '?'} size={32} />
        )}
      </span>
      <div
        className={`min-w-0 px-3 py-2 text-[13.5px] leading-[18px] ${mine ? 'chat-bubble-mine' : 'chat-bubble-theirs'} ${m.isDeleted ? 'opacity-80' : ''} ${selected ? 'ring-2 ring-primary/70' : ''} ${highlighted ? 'ring-2 ring-primary shadow-lg' : ''} relative transition-shadow duration-500`}
        style={messageBubbleStyle(bubbleLayout)}
      >
        {/* Панель действий: над баблом, ОДИН горизонтальный ряд (4 главных +
            «Ещё»); стекло — .glass-input. На тач — по тапу (actionsOpen). */}
        {!m.isDeleted && !selectMode && (
          <MessageActionsPanel
            open={actionsOpen}
            copyText={bodyText}
            onReply={() => { onReply(m.id); closeActions(); }}
            onReact={() => setPickerOpen((v) => !v)}
            onForward={() => { onForward(m); closeActions(); }}
            onMore={(x, y) => { onMore?.(m, x, y); closeActions(); }}
          />
        )}
        <ReactionPicker
          open={pickerOpen && !m.isDeleted && !selectMode}
          rxVersion={rxVersion}
          onPick={(code) => { onReactToggle(m, code); closeActions(); }}
        />
        {!mine && !grouped && <div className="mb-0.5 max-w-full break-words text-[11px] font-medium text-primary">{senderName(m.senderId)}</div>}
        {m.replyTo && !m.isDeleted && replySourceText && (
          <div className={`mb-1 max-w-full truncate border-l-2 pl-1.5 text-[9px] ${mine ? 'border-primary/40 text-primary/80' : 'border-muted-foreground/40 text-muted-foreground'}`}>
            {replySourceText}
          </div>
        )}
        {!!bodyText && !voiceTranscript && (
          <div className="min-w-0 max-w-full [overflow-wrap:anywhere] [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_table]:max-w-full">
            {looksLikeMarkdown(bodyText) ? (
              // Markdown-разметка рендерится как раньше (упоминания не подсвечиваем —
              // не ломаем MD); простой текст — ссылки + подсветка «@login» участников
              <MarkdownView text={bodyText} />
            ) : (
              <div className="whitespace-pre-wrap">
                <MentionRichText text={bodyText} logins={mentionLogins || []} myLogin={myLogin} mine={mine} highlight={highlightQuery} />
              </div>
            )}
          </div>
        )}
        {/* Геометка (черновик): канонический текст → локальное превью-карта */}
        {!m.isDeleted && (() => {
          const geo = parseGeoMessage(bodyText);
          return geo ? <GeoMessagePreview point={geo} mine={mine} /> : null;
        })()}
        {assistantAnswer && !m.isDeleted && <AnswerSources sources={parsedText.sources} assistantAnswer={assistantAnswer} />}
        {attachments.length > 0 && (
          <MessageAttachments
            attachments={attachments}
            mine={mine}
            onDownload={onDownload}
            onOpenPreview={openPreview}
            autoPlayFileId={autoPlayFileId}
            onAudioEnded={onAudioEnded}
            onAudioInterrupt={onAudioInterrupt}
            onAudioAutoplayConsumed={onAudioAutoplayConsumed}
          />
        )}
        {voiceTranscript && (
          <ChatTranscript
            text={bodyText}
            mine={mine}
            mentionLogins={mentionLogins}
            myLogin={myLogin}
            highlightQuery={highlightQuery}
          />
        )}
        <MessageReactionsRow
          reactions={m.reactions}
          myId={myId}
          rxVersion={rxVersion}
          senderName={senderName}
          onToggle={(emoji) => onReactToggle(m, emoji)}
        />
        {m.isDeleted && (
          <div className="text-[9px] mt-0.5 uppercase tracking-wide opacity-80">помечено на удаление</div>
        )}
        {!m.isDeleted && (m.replyCount || 0) > 0 && onOpenThread && (
          <button
            className="mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] text-muted-foreground hover:text-foreground hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            style={{ borderColor: 'var(--color-border)' }}
            onClick={(ev) => { ev.stopPropagation(); onOpenThread(m); }}
          >
            <MessageCircle className="h-3 w-3" /> {m.replyCount} ответов
          </button>
        )}
        <div className={`mt-1 flex min-w-0 flex-wrap items-center justify-end gap-1.5 text-[11px] leading-none ${mine ? 'text-primary/75' : 'text-muted-foreground'}`}>
          {m.isPinned && (
            <span className="inline-flex items-center" title="Закреплённое сообщение">
              <Pin className="h-2.5 w-2.5" aria-label="Закреплено" />
            </span>
          )}
          {m.editedAt && (
            <span
              className="cursor-help"
              title={(m.editHistory?.length
                ? m.editHistory.map((e) => `${new Date(e.editedAt).toLocaleString('ru-RU')}: ${e.text}`).join('\n\n')
                : 'изменено')}
            >
              изменено
            </span>
          )}
          <span>{new Date(m.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
          {mine && (
            <span className="text-[11px] font-bold leading-none">{m.readBy?.some((id) => id !== myId) ? '✓✓' : '✓'}</span>
          )}
        </div>
      </div>
      </div>
      </div>
      {previewFile && (
        <FilePreviewModal fileId={previewFile.fileId} fileName={previewFile.fileName} onClose={() => setPreviewFile(null)} />
      )}
    </div>
  );
}

export const MessageBubble = memo(MessageBubbleInner);

